import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  ImprovProfile,
  ImprovDimension,
  FieldPolicy,
  FieldPolicyKind,
  AudienceMode,
  ResolverPolicy,
} from '@stage/contracts';
import { ApiError } from '../api/client.js';
import type { ProfileInput } from '../api/improvClient.js';
import { useImprovClient } from '../hooks/useImprovClient.js';

/**
 * Improv-profile editor (M5, §20.10): configure which musical dimensions the audience may control
 * for "Create a Song", each with a per-dimension field policy (§20.2). Supports a default audience
 * mode, an advanced-drilldown toggle, a resolver policy, an optional summary template and device
 * profile reference, and publishing a new active version (§20.10).
 */

const DIMENSIONS: ReadonlyArray<{ value: ImprovDimension; label: string }> = [
  { value: 'tempoCategory', label: 'Tempo category' },
  { value: 'exactBpm', label: 'Exact BPM' },
  { value: 'tempoRange', label: 'Tempo range' },
  { value: 'tonic', label: 'Tonic' },
  { value: 'mode', label: 'Mode / tonality' },
  { value: 'meter', label: 'Meter' },
  { value: 'grooveFamily', label: 'Groove family' },
  { value: 'rhythmPattern', label: 'Rhythm pattern' },
  { value: 'rhythmVariation', label: 'Rhythm variation' },
  { value: 'drumKit', label: 'Drum kit' },
  { value: 'energy', label: 'Energy' },
  { value: 'mood', label: 'Mood' },
  { value: 'genre', label: 'Genre / style' },
  { value: 'topic', label: 'Topic / theme' },
  { value: 'requiredPhrase', label: 'Required word or phrase' },
  { value: 'instrumentEmphasis', label: 'Instrument emphasis' },
  { value: 'performerNote', label: 'Performer note' },
];

const KINDS: ReadonlyArray<{ value: FieldPolicyKind; label: string }> = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'fixed', label: 'Fixed (artist value)' },
  { value: 'general', label: 'General (friendly category)' },
  { value: 'guided', label: 'Guided (curated choices)' },
  { value: 'exact', label: 'Exact (audience enters)' },
  { value: 'random', label: 'Random (system picks)' },
  { value: 'artist_decides', label: 'Artist decides' },
];

const AUDIENCE_MODES: ReadonlyArray<{ value: AudienceMode; label: string }> = [
  { value: 'general', label: 'General' },
  { value: 'guided', label: 'Guided' },
  { value: 'advanced', label: 'Advanced' },
];

const RESOLVER_POLICIES: ReadonlyArray<{ value: ResolverPolicy; label: string }> = [
  { value: 'artist_review', label: 'Artist review (leave exact unresolved)' },
  { value: 'deterministic_default', label: 'Deterministic default' },
  { value: 'seeded_random', label: 'Seeded random (by brief ID)' },
  { value: 'audience_exact', label: 'Audience exact when valid' },
];

interface PolicyDraft {
  dimension: ImprovDimension;
  kind: FieldPolicyKind;
  allowedChoicesText: string;
  defaultValue: string;
  required: boolean;
  helpText: string;
  artistCanOverride: boolean;
}

interface ProfileDraft {
  profileId: string | null;
  name: string;
  audienceMode: AudienceMode;
  advancedDrilldown: boolean;
  resolverPolicy: ResolverPolicy;
  summaryTemplate: string;
  deviceProfileRef: string;
  policies: PolicyDraft[];
}

function emptyPolicy(): PolicyDraft {
  return {
    dimension: 'tempoCategory',
    kind: 'general',
    allowedChoicesText: '',
    defaultValue: '',
    required: false,
    helpText: '',
    artistCanOverride: true,
  };
}

const EMPTY_DRAFT: ProfileDraft = {
  profileId: null,
  name: '',
  audienceMode: 'general',
  advancedDrilldown: false,
  resolverPolicy: 'artist_review',
  summaryTemplate: '',
  deviceProfileRef: '',
  policies: [emptyPolicy()],
};

function splitCsv(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function policyToDraft(p: FieldPolicy): PolicyDraft {
  return {
    dimension: p.dimension,
    kind: p.kind,
    allowedChoicesText: p.allowedChoices.join(', '),
    defaultValue: p.defaultValue ?? '',
    required: p.required,
    helpText: p.helpText ?? '',
    artistCanOverride: p.artistCanOverride,
  };
}

function draftToPolicy(p: PolicyDraft): FieldPolicy {
  return {
    dimension: p.dimension,
    kind: p.kind,
    allowedChoices: splitCsv(p.allowedChoicesText),
    defaultValue: p.defaultValue.trim() ? p.defaultValue.trim() : null,
    required: p.required,
    helpText: p.helpText.trim() ? p.helpText.trim() : undefined,
    artistCanOverride: p.artistCanOverride,
  };
}

function draftToInput(draft: ProfileDraft): ProfileInput {
  return {
    name: draft.name.trim(),
    audienceMode: draft.audienceMode,
    advancedDrilldown: draft.advancedDrilldown,
    resolverPolicy: draft.resolverPolicy,
    fieldPolicies: draft.policies.map(draftToPolicy),
    ...(draft.summaryTemplate.trim() ? { summaryTemplate: draft.summaryTemplate.trim() } : {}),
    deviceProfileRef: draft.deviceProfileRef.trim() ? draft.deviceProfileRef.trim() : null,
  };
}

export function ImprovProfilePage() {
  const { artistId = '' } = useParams();
  const client = useImprovClient();

  const [profiles, setProfiles] = useState<ImprovProfile[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [draft, setDraft] = useState<ProfileDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setProfiles(await client.listProfiles(artistId));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load profiles.');
    } finally {
      setLoading(false);
    }
  }, [client, artistId]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = useCallback(() => {
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  }, []);

  const editProfile = useCallback((profile: ImprovProfile) => {
    setDraft({
      profileId: profile.improvProfileId,
      name: profile.name,
      audienceMode: profile.audienceMode,
      advancedDrilldown: profile.advancedDrilldown,
      resolverPolicy: profile.resolverPolicy,
      summaryTemplate: profile.summaryTemplate ?? '',
      deviceProfileRef: profile.deviceProfileRef ?? '',
      policies:
        profile.fieldPolicies.length > 0
          ? profile.fieldPolicies.map(policyToDraft)
          : [emptyPolicy()],
    });
    setFormError(null);
    setNotice(null);
  }, []);

  const patchPolicy = (index: number, patch: Partial<PolicyDraft>) =>
    setDraft((d) => ({
      ...d,
      policies: d.policies.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    }));

  const addPolicy = () =>
    setDraft((d) => ({ ...d, policies: [...d.policies, emptyPolicy()] }));

  const removePolicy = (index: number) =>
    setDraft((d) => ({ ...d, policies: d.policies.filter((_, i) => i !== index) }));

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!draft.name.trim()) return;
      setSaving(true);
      setFormError(null);
      setNotice(null);
      try {
        const input = draftToInput(draft);
        if (draft.profileId) {
          const updated = await client.updateProfile(draft.profileId, input);
          setProfiles((prev) =>
            prev.map((p) => (p.improvProfileId === updated.improvProfileId ? updated : p)),
          );
          setNotice(`Saved “${updated.name}”.`);
        } else {
          const created = await client.createProfile(artistId, input);
          setProfiles((prev) => [created, ...prev]);
          setNotice(`Created “${created.name}”.`);
        }
        resetForm();
      } catch (err) {
        if (err instanceof ApiError) setFormError(err);
        else setFormError(null);
      } finally {
        setSaving(false);
      }
    },
    [client, artistId, draft, resetForm],
  );

  const onPublish = useCallback(
    async (profile: ImprovProfile) => {
      setBusyId(profile.improvProfileId);
      setNotice(null);
      try {
        const updated = await client.publishProfileVersion(profile.improvProfileId);
        setProfiles((prev) =>
          prev.map((p) => (p.improvProfileId === updated.improvProfileId ? updated : p)),
        );
        setNotice(`Published “${updated.name}” v${updated.profileVersion}.`);
      } catch (err) {
        setLoadError(err instanceof ApiError ? err.message : 'Could not publish version.');
      } finally {
        setBusyId(null);
      }
    },
    [client],
  );

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to="/app">
              Overview
            </Link>{' '}
            / Improv profiles
          </p>
          <h1>Improv profiles</h1>
          <p className="muted">
            Configure which musical dimensions the audience can control in Create a Song.
          </p>
        </div>
      </header>

      {loadError && <p className="error">{loadError}</p>}

      <section className="card">
        <div className="module-head">
          <h2>{draft.profileId ? 'Edit profile' : 'New profile'}</h2>
          {notice && (
            <span className="muted" aria-live="polite">
              {notice}
            </span>
          )}
        </div>
        <form className="profile-form" onSubmit={onSubmit}>
          <div className="profile-form__grid">
            <label>
              Name
              <input
                value={draft.name}
                required
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Interactive Looper"
              />
            </label>
            <label>
              Default audience mode
              <select
                value={draft.audienceMode}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, audienceMode: e.target.value as AudienceMode }))
                }
              >
                {AUDIENCE_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Resolver policy
              <select
                value={draft.resolverPolicy}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, resolverPolicy: e.target.value as ResolverPolicy }))
                }
              >
                {RESOLVER_POLICIES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Device profile ref (optional)
              <input
                value={draft.deviceProfileRef}
                onChange={(e) => setDraft((d) => ({ ...d, deviceProfileRef: e.target.value }))}
                placeholder="e.g. boss-rc600"
                spellCheck={false}
                autoCapitalize="none"
              />
            </label>
          </div>

          <label>
            Summary template (optional)
            <input
              value={draft.summaryTemplate}
              onChange={(e) => setDraft((d) => ({ ...d, summaryTemplate: e.target.value }))}
              placeholder="A {tempo} song in {key} using a {meter} {groove} groove."
              maxLength={280}
            />
          </label>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={draft.advancedDrilldown}
              onChange={(e) =>
                setDraft((d) => ({ ...d, advancedDrilldown: e.target.checked }))
              }
            />
            Advanced drilldown available
          </label>

          <fieldset className="policy-editor">
            <legend>Field policies</legend>
            {draft.policies.map((policy, index) => (
              <div key={index} className="policy-row">
                <div className="policy-row__grid">
                  <label>
                    Dimension
                    <select
                      value={policy.dimension}
                      onChange={(e) =>
                        patchPolicy(index, { dimension: e.target.value as ImprovDimension })
                      }
                    >
                      {DIMENSIONS.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Policy
                    <select
                      value={policy.kind}
                      onChange={(e) =>
                        patchPolicy(index, { kind: e.target.value as FieldPolicyKind })
                      }
                    >
                      {KINDS.map((k) => (
                        <option key={k.value} value={k.value}>
                          {k.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Allowed choices (comma-separated)
                    <input
                      value={policy.allowedChoicesText}
                      onChange={(e) =>
                        patchPolicy(index, { allowedChoicesText: e.target.value })
                      }
                      placeholder="e.g. rock, funk, jazz"
                    />
                  </label>
                  <label>
                    Default
                    <input
                      value={policy.defaultValue}
                      onChange={(e) => patchPolicy(index, { defaultValue: e.target.value })}
                    />
                  </label>
                  <label>
                    Public help text
                    <input
                      value={policy.helpText}
                      onChange={(e) => patchPolicy(index, { helpText: e.target.value })}
                      maxLength={280}
                    />
                  </label>
                </div>
                <div className="policy-row__toggles">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={policy.required}
                      onChange={(e) => patchPolicy(index, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={policy.artistCanOverride}
                      onChange={(e) =>
                        patchPolicy(index, { artistCanOverride: e.target.checked })
                      }
                    />
                    Artist can override
                  </label>
                  <button
                    type="button"
                    className="ghost icon-btn"
                    onClick={() => removePolicy(index)}
                    disabled={draft.policies.length <= 1}
                    aria-label="Remove dimension"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button type="button" className="ghost policy-editor__add" onClick={addPolicy}>
              + Add dimension
            </button>
          </fieldset>

          <div className="button-row">
            <button
              type="submit"
              className="action--primary"
              disabled={saving || !draft.name.trim()}
            >
              {draft.profileId ? 'Save changes' : 'Create profile'}
            </button>
            {draft.profileId && (
              <button type="button" className="ghost" onClick={resetForm} disabled={saving}>
                Cancel edit
              </button>
            )}
          </div>

          {formError && (
            <div className="validation" role="alert">
              <p className="error">{formError.message}</p>
              {formError.fieldErrors.length > 0 && (
                <ul>
                  {formError.fieldErrors.map((fe, i) => (
                    <li key={`${fe.path}-${i}`}>
                      <code>{fe.path}</code>: {fe.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </form>
      </section>

      <section className="card">
        <h2>Profiles</h2>
        {profiles.length === 0 && !loading ? (
          <p className="muted">No profiles yet. Create one above.</p>
        ) : (
          <ul className="profile-list">
            {profiles.map((profile) => (
              <li key={profile.improvProfileId} className="profile-card">
                <div className="profile-card__head">
                  <div>
                    <h3 className="profile-card__name">{profile.name}</h3>
                    <p className="muted profile-card__meta">
                      v{profile.profileVersion} · {profile.audienceMode} ·{' '}
                      {profile.fieldPolicies.length} field
                      {profile.fieldPolicies.length === 1 ? '' : 's'}
                      {profile.deviceProfileRef ? ` · ${profile.deviceProfileRef}` : ''}
                    </p>
                  </div>
                  <span className={`health-badge health-badge--${profile.status}`}>
                    {profile.status}
                  </span>
                </div>
                <div className="button-row">
                  <button type="button" className="ghost" onClick={() => editProfile(profile)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void onPublish(profile)}
                    disabled={busyId === profile.improvProfileId}
                  >
                    Publish new version
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {loading && <p aria-busy="true">Loading…</p>}
      </section>
    </main>
  );
}
